/**
 * The Production Records timeline on a PHONE (plan-06-mobile.md, lane A):
 * the Road Warrior pattern (IMG_0197 / IMG_0198), never the desktop row
 * crushed into 340px (IMG_0195 / IMG_0196).
 *
 *   - `MobileDayTimeline`: a dated rail over `CampusDayGroup[]`. Every day is
 *     a header (dot, `TODAY · MON · SEP 21`) with one card per campus hanging
 *     off a `border-l-2` rail. BOTH single- and multi-campus schools use it:
 *     the day header IS the timeline, so a single campus needs no date on its
 *     card. The header's rollup Badge ("All campuses complete", "2 campuses
 *     incomplete") renders only for a multi-campus school; a single campus
 *     shows its status on the card.
 *   - `MobileDayCard`: stacked, never a row. Row 1 = the mealtimes (small,
 *     muted) left and the status Badge right; row 2 = the campus name; row 3
 *     (under a rule) = who signed / the entry count / why nothing can open
 *     yet; row 4 = full-width actions.
 *   - `MobileRecordsLoading`: the skeleton, mirroring that card block-for-block.
 *
 * The dot / rail / header geometry and the card's header-row, title and
 * `border-t` detail-row classes are `@ordo/ui/event-timeline`'s, verbatim, so
 * this page reads like the Events page on the same phone. Bespoke rather than
 * that primitive for the same reason the desktop timeline is: the primitive
 * cannot badge a day or give a card its own actions.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Skeleton } from '@ordo/ui/skeleton'
import { Check, Printer } from 'lucide-react'

import type { ProductionRecordDayWire } from '@/api/production-records'
import { DayStatusGlyph } from '@/components/production/day-status-glyph'
import {
  type CampusDayGroup,
  dayGroupStatus,
  type DayProgress,
  firstRecordId,
  mealtimesLine,
  signedLine,
} from '@/lib/production-records/day-progress'
import { daySectionId, formatDayGroupLabel, relativeDayLabel } from '@/lib/production-records/day-tasks'
import { mobileCardState, mobileDetailLine, mobileStatusBadge } from '@/lib/production-records/mobile-timeline'

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export interface MobileDayCardProps {
  /** The card's identity; the day is in the header above it. */
  campusName: string
  progress: DayProgress
  /** "Breakfast · Lunch" */
  mealtimesLine: string
  /** "Signed yesterday by Shannon Talmage"; null while nothing is signed. */
  signedLine: string | null
  /** Get started / Continue: opens the unified record for this campus-day. */
  onOpen: () => void
  /** View details: the day's record page. Omitted when the day has no record to show. */
  onView?: (() => void) | null
  onPrint: () => void
}

export function MobileDayCard({
  campusName,
  progress,
  mealtimesLine: mealtimes,
  signedLine: signed,
  onOpen,
  onView = null,
  onPrint,
}: MobileDayCardProps) {
  const badge = mobileStatusBadge(progress)
  const detail = mobileDetailLine(progress, signed)
  const action = progress.action
  return (
    <div
      role="group"
      aria-label={`${campusName}, ${badge.label.toLowerCase()}`}
      data-state={mobileCardState(progress)}
      className="block rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
        <p className="min-w-0 truncate text-xs text-muted-foreground">{mealtimes}</p>
        <Badge variant={badge.variant} data-testid="card-status" className="shrink-0 rounded px-2 py-0.5 text-[11px]">
          {badge.label}
        </Badge>
      </div>
      <h3 className="text-sm font-semibold text-foreground">{campusName}</h3>
      <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-xs">
        {progress.complete ? (
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-success-foreground">
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0">{detail}</span>
          </p>
        ) : (
          <p className="flex min-w-0 items-center gap-2 text-muted-foreground">
            <DayStatusGlyph progress={progress} size="sm" />
            <span className="min-w-0">{detail}</span>
          </p>
        )}
      </div>
      {progress.complete ? (
        <div className={cn('mt-3 grid gap-2', onView ? 'grid-cols-2' : 'grid-cols-1')}>
          {onView ? (
            <Button type="button" variant="secondary" onClick={onView}>
              View details
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onPrint}>
            <Printer aria-hidden />
            Print
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button type="button" size="icon" variant="outline" aria-label="Print blank forms" onClick={onPrint}>
            <Printer aria-hidden />
          </Button>
          {action ? (
            <Button
              type="button"
              disabled={!action.enabled}
              onClick={onOpen}
              className={cn('flex-1', !action.enabled && 'opacity-55')}>
              {action.label}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

export interface MobileDayTimelineProps {
  /** One group per service day, newest first, one row per campus (`groupCampusDays`). */
  groups: ReadonlyArray<CampusDayGroup>
  /** Two or more campuses: the day header wears the rollup Badge. A single campus shows its status on the card. */
  multiCampus?: boolean
  /** The card's parts, percent and action, with the page's local drafts folded in. */
  progressFor: (locationId: string, day: ProductionRecordDayWire) => DayProgress
  /** Get started / Continue: the unified record for that campus-day. */
  onOpen: (locationId: string, day: ProductionRecordDayWire) => void
  /** View details: the record page for that campus-day (its first record; the page shows the rest as tabs). */
  onView: (locationId: string, recordId: string) => void
  onPrint: (locationId: string, serviceDate: string) => void
  now?: Date
}

export function MobileDayTimeline({
  groups,
  multiCampus = false,
  progressFor,
  onOpen,
  onView,
  onPrint,
  now = new Date(),
}: MobileDayTimelineProps) {
  return (
    <div className="space-y-6" data-layout="mobile">
      {groups.map((group) => {
        const label = formatDayGroupLabel(group.serviceDate, now)
        const status = dayGroupStatus(group)
        const today = relativeDayLabel(group.serviceDate, now) === 'Today'
        return (
          <section
            key={group.serviceDate}
            id={daySectionId(group.serviceDate)}
            aria-label={multiCampus ? `${label}, ${status.label.toLowerCase()}` : label}>
            <div className="mb-3 flex items-center gap-2 px-1">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-full', today ? 'bg-primary' : 'bg-muted-foreground/60')}
                aria-hidden="true"
              />
              <h2
                className={cn(
                  'text-xs font-semibold uppercase tracking-wider',
                  today ? 'text-primary' : 'text-muted-foreground',
                )}>
                {label}
              </h2>
              {multiCampus ? (
                <Badge
                  variant={status.tone === 'complete' ? 'success' : 'warning'}
                  data-testid="day-rollup"
                  className="ml-auto shrink-0 rounded px-2 py-0.5 text-[11px]">
                  {status.label}
                </Badge>
              ) : null}
            </div>
            <div className={cn('ml-1 space-y-3 border-l-2 pl-5', today ? 'border-primary/20' : 'border-border')}>
              {group.rows.map(({ campus, day }) => {
                const recordId = firstRecordId(day)
                return (
                  <MobileDayCard
                    key={campus.id}
                    campusName={campus.name}
                    progress={progressFor(campus.id, day)}
                    mealtimesLine={mealtimesLine(day)}
                    signedLine={signedLine(day, now)}
                    onOpen={() => onOpen(campus.id, day)}
                    onView={recordId ? () => onView(campus.id, recordId) : null}
                    onPrint={() => onPrint(campus.id, day.serviceDate)}
                  />
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton (mirrors the mobile card block-for-block)
// ---------------------------------------------------------------------------

export function MobileRecordsLoading() {
  // Three days: the dot + header label, then one card off the rail — the
  // meta / badge row, the campus name, the rule, the detail line, two
  // full-width actions.
  return (
    <div className="space-y-6" data-testid="mobile-records-loading">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i}>
          <div className="mb-3 flex items-center gap-2 px-1">
            <Skeleton className="h-2.5 w-2.5 shrink-0 rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="ml-1 space-y-3 border-l-2 border-border pl-5">
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
              <Skeleton className="h-4 w-40" />
              <div className="mt-3 border-t border-border pt-3">
                <Skeleton className="h-3.5 w-44" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Skeleton className="h-9 rounded-md" />
                <Skeleton className="h-9 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
